import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { selectCurrentViewState, selectViewStateFilters } from '@common/store/navigation/selectors/viewstate.selectors';
import { DiscoverSearchService } from '@discover/services/discover-search-service';
import { DiscoverService } from '@discover/services/discover-service';
import { WireActions, WireApiActions } from '@discover/store/actions/discover.actions';
import { selectSelectedImages, selectTreeControl } from '@discover/store/selectors/discover.selectors';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { WireSearchViewStateRecord } from '@src/suite/common/data-models/records/wire-search-viewstate-record';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { LayoutState } from '@src/suite/common/services/layout-state';
import { OpenFullscreenModalActions } from '@src/suite/common/store/fullscreen';
import { ListActions } from '@src/suite/common/store/list/list.actions';
import { ApplicationActions } from '@src/suite/common/store/navigation/actions/application.actions';
import { PlaybookApiActions } from '@src/suite/common/store/playbook/playbook.actions';
import { navigateTo, navigateToImage } from '@src/suite/common/utils/store-utils';
import { DiscoverItemAttachToDialogComponent } from '@src/suite/discover/components/discover-item-attach-to/discover-item-attach-to-dialog';
import { PlaybookSearchService } from '@src/suite/playbook/services/playbook-search-service';
import { from, of, timer } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, take, takeUntil } from 'rxjs/operators';

@Injectable()
export class DiscoverEffects {
    private actions$: Actions = inject(Actions);
    private discoverSearchService: DiscoverSearchService = inject(DiscoverSearchService);
    private discoverService: DiscoverService = inject(DiscoverService);
    private dialog: MatDialog = inject(MatDialog);
    private store: Store = inject(Store);
    private viewStateService: LayoutState = inject(LayoutState);
    private playbookSearchService: PlaybookSearchService = inject(PlaybookSearchService);

    startPolling$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(WireApiActions.startPolling),
            concatLatestFrom(() => [this.store.select(selectCurrentViewState), this.store.select(selectViewStateFilters)]),
            switchMap(([action, vs, filters]) => {
                return timer(0, action.interval).pipe(
                    exhaustMap(() => {
                        if (vs instanceof WireSearchViewStateRecord) {
                            return this.discoverSearchService.searchWires(vs, filters).pipe(
                                map(res => WireApiActions.loadWiresSuccess({ wires: res.results as StorytellingWireRecord[] })),
                                catchError(error => of(WireApiActions.loadWiresError({ error })))
                            );
                        } else {
                            return this.discoverSearchService.loadWires(vs, filters).pipe(
                                map(data => WireApiActions.loadWiresSuccess({ wires: data })),
                                catchError(error => of(WireApiActions.loadWiresError({ error })))
                            );
                        }
                    }),
                    takeUntil(this.actions$.pipe(ofType(WireApiActions.stopPolling)))
                );
            })
        );
    });

    stopPolling$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(WireApiActions.stopPolling),
            switchMap(action => {
                if (action.restart) {
                    return of(WireApiActions.startPolling({ interval: action.interval }));
                } else {
                    return of(ApplicationActions.noOpAction());
                }
            })
        );
    });

    searchWires$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(WireActions.searchWires),
            concatLatestFrom(() => this.store.select(selectViewStateFilters)),
            switchMap(([action, filters]) => {
                return this.discoverSearchService.searchWires(action.vs, filters).pipe(
                    map(res => WireApiActions.loadWiresSuccess({ wires: res.results as StorytellingWireRecord[] })),
                    catchError(error => of(WireApiActions.loadWiresError({ error })))
                );
            })
        );
    });

    navigateToWire$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(WireActions.navigateToWire),
            concatLatestFrom(() => this.store.select(selectTreeControl)),
            map(([action, treecontrol]) => {
                const nextItem = navigateTo(action.direction, action.currentNodeUuid, treecontrol);
                return ListActions.navigateObject({ nodeUuid: nextItem.uniqueId });
            })
        );
    });

    openMedia$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(WireActions.navigateToImage),
            concatLatestFrom(() => this.store.select(selectSelectedImages)),
            map(([action, images]) => {
                const nextItem = navigateToImage(action.direction, action.currentImage, images);
                return WireActions.openImage({ image: nextItem });
            })
        );
    });

    createWire$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(WireApiActions.createTopic),
            map(action => {
                return OpenFullscreenModalActions.initCreateNewRecordFullscreen({
                    recordType: ObjectTypes.TOPIC,
                    wireRecord: action.wire,
                });
            })
        );
    });

    openNewTopicFromWire$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(WireActions.openNewTopicFromWire),
            exhaustMap(data => {
                this.dialog.closeAll();
                return this.dialog.afterAllClosed.pipe(
                    take(1),
                    map(() => {
                        //TODO remove topicRecord argument from this action when we decide what we want to do when creating a new topic from a wire
                        return OpenFullscreenModalActions.openTopicFullscreenDialog({ recordGuid: data.topic?.Guid, recordType: data.topic?.Type, topicRecord: data.topic });
                    })
                );
            })
        );
    });

    openAttachToTopic$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(WireActions.openAttachToDialog),
                exhaustMap(data => {
                    this.dialog.closeAll();
                    return this.dialog.afterAllClosed.pipe(
                        take(1),
                        map(() => {
                            this.dialog.open(DiscoverItemAttachToDialogComponent, {
                                width: '35rem',
                                height: '80vh',
                                hasBackdrop: true,
                                disableClose: true,
                                data: data.wire,
                            });
                        })
                    );
                })
            );
        },
        { dispatch: false }
    );

    createItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(WireApiActions.createDiscoverItem),
            switchMap(action => {
                return from(this.discoverService.createDiscoverItem(action.wire)).pipe(
                    map(res => {
                        if (res.status) {
                            return PlaybookApiActions.createTopic({
                                topic: action.topic,
                                createAndNext: action.createAndNext,
                                discoverItemDatabase: res.data.Database,
                                discoverItemPath: res.data.Path,
                                discoverItemGuid: res.data.Guid,
                            });
                        }
                    }),
                    catchError(error => of(WireApiActions.createDiscoverItemError({ error })))
                );
            })
        );
    });

    attachItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(WireApiActions.attachItem),
            switchMap(action => {
                return from(this.discoverService.attachDiscoverItem(action.wire, action.topic));
            }),
            map(() => {
                this.dialog.closeAll();
                return WireApiActions.attachItemSuccess();
            })
        );
    });

    topicSearchTerm$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(WireApiActions.topicSearchTerm),
            switchMap(action => {
                if (action.searchTerm?.trim()) {
                    return from(this.discoverService.getSavedViewItems(action.searchTerm)).pipe(
                        map(data => WireApiActions.loadTopicsSuccess({ topics: data })),
                        catchError(error => of(WireApiActions.loadWiresError({ error })))
                    );
                }
                return of(WireApiActions.loadTopicsSuccess({ topics: new Map<string, StorytellingTopicRecord>() }));
            })
        );
    });

    loadTopicViewStates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(WireApiActions.loadTopicViewStates),
            switchMap(() => {
                return from(this.viewStateService.getViewStateRecords('playbook')).pipe(
                    switchMap(({ data }) => {
                        return [WireApiActions.loadTopicViewStatesSuccess({ data }), WireActions.setCurrentTopicViewState({ viewState: data?.views[0] })];
                    }),
                    catchError(error => of(WireApiActions.loadWiresError({ error })))
                );
            })
        );
    });

    loadTopics$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(WireActions.setCurrentTopicViewState, WireApiActions.reloadTopicsBasedOnCurrentViewState),
            switchMap(action => {
                if (action?.viewState?.guid) {
                    return from(this.playbookSearchService.getPlaybookResults(action.viewState)).pipe(
                        map(data => data?.data?.topics),
                        map(topics => WireApiActions.loadTopicsSuccess({ topics })),
                        catchError(error => of(WireApiActions.loadWiresError({ error })))
                    );
                }
                return of(WireApiActions.loadTopicsSuccess({ topics: new Map<string, StorytellingTopicRecord>() }));
            })
        );
    });
}
